import { ChangeDetectorRef, Component, ContentChild, inject, Input, input } from '@angular/core';
import { ButtonStyles, ButtonTypes } from '../../button/button.component';
import { ZiroIcons } from '../models/ziro-icons.enum';
import { ZiroFieldAbstractDirective } from '../../forms/ziro-field-abstract.directive';
import {
  HtmlInputAddOn,
  ZiroFieldComponentConfig,
  ZiroFormsMessage,
  ZiroFormsValidationState,
} from '../../forms/ziro-forms-models';
import { AcceptedFileExtensions } from '../../forms/fields/file-upload/ziro-file-upload.component';
import { isNil } from 'lodash';
import { ZiroSubmitButtonComponent } from '../../forms/submit-button/ziro-submit-button.component';
import { DatatableColumn, DatatableRow } from '../tables/datatable/datatable.models';

export interface ZiroFile<METADATA = unknown> {
  id: string;
  file: File;
  metadata?: METADATA;
}

export enum UploaderType {
  STAGED = 'STAGED',
  TABLE = 'TABLE',
}

export type MetadataFn<T> = (file: File) => T;

export class ZiroFileUploaderConfig<METADATA> extends ZiroFieldComponentConfig {
  constructor(
    public config: {
      uploaderType: UploaderType;
      acceptedFileExtensions?: AcceptedFileExtensions;
      maxKbSize?: number;
      metadataFn?: MetadataFn<METADATA>; // use this to get some metadata and display it when the file is selected
      maxFileNameLength?: number;
    },
  ) {
    super();
  }
}

@Component({
  selector: 'ziro-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: FileUploaderComponent }],
  standalone: false,
})
export class FileUploaderComponent<ROW extends DatatableRow, METADATA> extends ZiroFieldAbstractDirective<
  ZiroFile<METADATA>[],
  ZiroFile<METADATA>[],
  ZiroFileUploaderConfig<METADATA>
> {
  private changeDetectorRef = inject(ChangeDetectorRef);

  @ContentChild(ZiroSubmitButtonComponent) submitButton: ZiroSubmitButtonComponent;
  readonly uniqueRowIdentifier = input<keyof ROW>(null);
  readonly tableRows = input<ROW[]>([]);
  readonly showTable = input(false);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set columns(columns: DatatableColumn<ROW>[]) {
    this._columns = columns;
  }

  get columns(): DatatableColumn<ROW>[] {
    return this._columns;
  }

  _columns: DatatableColumn<ROW>[];
  files: ZiroFile<METADATA>[] = [];
  isFileOver: boolean;
  htmlInputAddOn: HtmlInputAddOn;
  formattedSize: string;
  invalidFileIds: string[] = [];
  uploaderType: UploaderType;
  formattedAcceptedFields: string;
  acceptedFileExtensions: AcceptedFileExtensions;
  maxFileNameLength: number;

  private _maxSizeInBytes = 0;
  private metadataFn: MetadataFn<METADATA>;

  protected readonly ButtonTypes = ButtonTypes;
  protected readonly ButtonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;

  private validators = {
    isWithinSizeConstraint: {
      validator: (files: ZiroFile[]): ZiroFormsValidationState => {
        let ziroValidationState: ZiroFormsValidationState;

        files?.forEach(({ file, id }) => {
          const isValid = !file || file?.size <= this._maxSizeInBytes;
          ziroValidationState = isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
          if (!isValid && !this.invalidFileIds.includes(id)) {
            this.invalidFileIds.push(id);
          }
        });

        if (this.invalidFileIds.length) {
          ziroValidationState = ZiroFormsValidationState.INVALID;
        }

        return ziroValidationState;
      },
      message: (): ZiroFormsMessage => {
        return {
          content: 'tkey;shared.fileselect.validators.max_size',
          params: {
            size: this._formatFileSize(this._maxSizeInBytes),
          },
        };
      },
    },
    isAcceptedFileType: {
      validator: (files: ZiroFile[]): ZiroFormsValidationState => {
        let ziroValidationState: ZiroFormsValidationState;

        files?.forEach(({ file, id }) => {
          const fileType = file.name.substring(file.name.lastIndexOf('.'));
          const isValid = this.acceptedFileExtensions.acceptedExtensions
            .split(',')
            .some((type) => type.trim() === fileType);
          ziroValidationState = isValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
          if (!isValid && !this.invalidFileIds.includes(id)) {
            this.invalidFileIds.push(id);
          }
        });

        if (this.invalidFileIds.length) {
          ziroValidationState = ZiroFormsValidationState.INVALID;
        }

        return ziroValidationState;
      },
      message: (): ZiroFormsMessage => {
        return {
          content: 'tkey;shared.fileselect.validators.strict_file_types',
          params: { types: this.acceptedFileExtensions.acceptedExtensions.replace(/\s*,\s*/g, '/') },
        };
      },
    },
  };

  applyComponentConfig: ({ config }: ZiroFileUploaderConfig<METADATA>) => void = ({
    config,
  }: ZiroFileUploaderConfig<METADATA>) => {
    // remove component-specific validators if they are already present, to preserve the proper order and prevent dupes
    this.config.validation = (this.config.validation || []).filter((validator) => {
      return validator !== this.validators.isWithinSizeConstraint && validator !== this.validators.isAcceptedFileType;
    });

    this.acceptedFileExtensions = isNil(config.acceptedFileExtensions)
      ? this.acceptedFileExtensions
      : config.acceptedFileExtensions;
    this._maxSizeInBytes = isNil(config.maxKbSize) ? this._maxSizeInBytes : config.maxKbSize * 1024;
    this.formattedSize = this._formatFileSize(this._maxSizeInBytes);

    this.formattedAcceptedFields = config.acceptedFileExtensions.acceptedExtensions.replaceAll(',', ' /');

    if (this._maxSizeInBytes) {
      this.config.validation = [this.validators.isWithinSizeConstraint, ...this.config.validation];
    }
    if (this.acceptedFileExtensions && !this.acceptedFileExtensions.allowOthers) {
      this.config.validation = [this.validators.isAcceptedFileType, ...this.config.validation];
    }

    this.uploaderType = config.uploaderType;
    this.metadataFn = config.metadataFn;
    this.maxFileNameLength = config.maxFileNameLength;
  };

  handleFiles(fileList: FileList): void {
    if (!fileList.length || this.state.disabled) {
      return;
    }

    this.deleteInvalidFiles();

    const file = Array.from(fileList).at(-1);
    const extensionRegex = /(?:\.([^.]+))?$/;
    const extensionType = extensionRegex.exec(file.name)[1];
    const renamedFile = new File(
      [file],
      !this.maxFileNameLength || file.name.length <= 50
        ? file.name
        : `${file.name.substring(0, this.maxFileNameLength - 4)}.${extensionType}`,
      {
        type: file.type,
      },
    );
    const newFile: ZiroFile<METADATA> = { id: Date.now().toString(), file: renamedFile };

    if (this.metadataFn) {
      newFile.metadata = this.metadataFn(file);
    }

    if (this.uploaderType === UploaderType.TABLE) {
      this.files = [];
    }
    this.files = [...this.files, newFile];

    this.changeDetectorRef.detectChanges();
    this.updateSelf(this.files);
  }

  onFileOver($event: boolean): void {
    this.isFileOver = $event;
  }

  getStagedFileSubtext(file: ZiroFile): string {
    const fileSize = this._formatFileSize(file.file?.size);
    if (file.metadata) {
      return file.metadata + ' | ' + fileSize;
    }
    return fileSize;
  }

  onDeleteFile(id: string): void {
    if (this.invalidFileIds.includes(id)) {
      const check = this.invalidFileIds.findIndex((identifier) => identifier === id);
      this.invalidFileIds.splice(check, 1);
    }
    this.files.splice(
      this.files.findIndex((f) => f.id === id),
      1,
    );

    this.changeDetectorRef.detectChanges();
    this.updateSelf(this.files);
  }

  private deleteInvalidFiles(): void {
    this.invalidFileIds.forEach((invalidFile) => {
      this.onDeleteFile(invalidFile);
    });
  }

  private _formatFileSize(bytes: number): string {
    const kb = 1024;
    const mb = Math.pow(kb, 2);
    const gb = Math.pow(kb, 3);

    if (bytes < kb) {
      return `${bytes} B`;
    }
    if (bytes < mb) {
      return `${this._roundToOneDecimalAsUnit(bytes, kb)} kB`;
    }
    if (bytes < gb) {
      return `${this._roundToOneDecimalAsUnit(bytes, mb)} MB`;
    }
    return `${this._roundToOneDecimalAsUnit(bytes, gb)} GB`;
  }

  private _roundToOneDecimalAsUnit(bytes: number, unit: number): number {
    return Math.floor((bytes * 10) / unit) / 10;
  }

  toEntity: (fieldData: ZiroFile<METADATA>[]) => ZiroFile<METADATA>[] = (fieldData: ZiroFile<METADATA>[]) => fieldData;

  toFieldData: (entity: ZiroFile<METADATA>[]) => ZiroFile<METADATA>[] = (entity: ZiroFile<METADATA>[]) => entity;
  protected readonly UploaderType = UploaderType;
}
